import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { STAGE_LABEL, caseFinished, labDayOf, type JobOrder } from '@/domain';
import { useJobOrders } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { failureWords } from '@/shared/failure/failure';
import { formatShortDay } from '@/shared/format/instant';
import { useMoney } from '@/ui/money/moneyView';
import { strings } from '@/shared/strings';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { StatusDot } from '@/ui/data/StatusDot';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { Button } from '@/ui/primitives/Button';
import { useCaseInPlace } from '@/features/orders/useCaseInPlace';
import { paymentSummaryPath } from '@/features/payments/paymentPaths';
import { WidgetSection } from '../WidgetSection';
import { ACTIVE, CASE_LISTS, type CaseListKey, type CaseListRule } from './caseLists';
import { Kpi, KpiStrip } from './FloorWidgets';
import board from '../WidgetBoard.module.css';

const text = strings.dashboard;

/**
 * A few cases of one list, with the way to all of them. The lab's request of
 * 12 October 2026: widgets related to a person's work.
 *
 * The rows are the rows of Needs attention: the job order and its item, who it
 * is for where the reader may see that and where it is otherwise, and at the
 * end what this list is about, its date, its balance or why it is stopped. A
 * row opens the case in place, over the dashboard, as a list on a doctor's
 * page does; a row of a money list opens the case's payment summary.
 *
 * It asks for six and says when there are more. It is offered only to somebody
 * who holds the screen the list is cut from (`mayShowWidget`), so View all
 * always has somewhere to go.
 */
export function CaseListWidget({ list }: { list: CaseListKey }) {
  const money = useMoney();
  const navigate = useNavigate();
  const rule = CASE_LISTS[list];
  const { data, isPending, isError, error } = useJobOrders(rule.filter);
  const place = useCaseInPlace();

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const title = text.widgets[list].title;

  const open = rule.money === true
    ? (order: JobOrder) => void navigate(paymentSummaryPath(order.jobOrderNo))
    : place.mayOpen
      ? (order: JobOrder) => place.open(order)
      : undefined;

  return (
    <WidgetSection
      label={rule.kicker}
      title={title}
      action={
        <Button variant="text" onClick={() => void navigate(rule.all)}>
          {text.viewAll}
        </Button>
      }
    >
      {isPending && <SkeletonRows rows={3} label={strings.common.loadingOrders} />}
      {isError && <p className={board.quiet}>{failureWords(error, strings.errors.server)}</p>}

      {data !== undefined &&
        (rows.length === 0 ? (
          <p className={board.quiet}>{text.empty[list]}</p>
        ) : (
          <>
            <ul className={board.rows} aria-label={title}>
              {rows.map((order) => (
                <li key={order.jobOrderNo}>
                  <CaseRow order={order} end={endOf(rule.end, order, money)} onOpen={open} />
                </li>
              ))}
            </ul>
            {total > rows.length && <p className={board.more}>{text.more(rows.length, total)}</p>}
          </>
        ))}
    </WidgetSection>
  );
}

function CaseRow({
  order,
  end,
  onOpen,
}: {
  order: JobOrder;
  end: ReactNode;
  onOpen: ((order: JobOrder) => void) | undefined;
}) {
  // Who the case is for, for a reader sent the names; where it is, for one who is not.
  const names = [order.dentistName, order.patientName].filter((name) => name != null && name !== '');
  const where =
    names.length > 0
      ? names.join(' · ')
      : order.currentStage === null
        ? ''
        : STAGE_LABEL[order.currentStage];

  const content = (
    <>
      <span className={board.what}>
        <span className={board.mono}>{order.jobOrderNo}</span>
        {order.product != null && order.product !== '' && <span>{order.product}</span>}
        {order.urgent && <StatusDot tone="urgent">{text.urgentMark}</StatusDot>}
      </span>
      <span className={board.who} dir="auto">
        {where}
      </span>
      {end}
    </>
  );

  if (onOpen === undefined) return <div className={board.row}>{content}</div>;
  return (
    <button type="button" className={board.row} onClick={() => onOpen(order)}>
      {content}
    </button>
  );
}

function endOf(kind: CaseListRule['end'], order: JobOrder, money: (value: number) => string): ReactNode {
  switch (kind) {
    case 'due':
      return (
        <span className={board.end}>
          <DeliveryDate date={order.deliveryDate} finished={caseFinished(order)} />
        </span>
      );
    case 'received':
      return (
        <span className={board.end}>
          {order.createdAt === undefined ? '' : formatShortDay(labDayOf(order.createdAt))}
        </span>
      );
    case 'hold':
      return (
        <span className={`${board.end} ${board.words}`} dir="auto" title={order.hold.reason ?? undefined}>
          {order.hold.reason ?? ''}
        </span>
      );
    case 'balance': {
      const owed = order.payment.balance ?? order.value;
      return <span className={board.end}>{owed == null ? '' : money(owed)}</span>;
    }
    case 'changes':
      return (
        <span className={`${board.end} ${board.words}`} title={order.accountsReview?.changes}>
          {order.accountsReview?.changes ?? ''}
        </span>
      );
  }
}

/**
 * The cases in hand, in figures: the active ones, the ones due today, the
 * overdue, the urgent and the ones on hold. For whoever holds Orders. Active
 * in place of all of them since the lab's request of 17 October 2026, item 3:
 * a case in Ready or Logistics is not active.
 *
 * Management reads these from the report's summary, on the figures of the
 * Overview. A desk that holds no report is answered the list of job orders,
 * and a list says how many it holds: so each figure is the size of the list a
 * card of the same name would open, counted by the server over the whole of
 * it. Due today, Overdue and On hold ask exactly what the list widgets of
 * those names ask, so a dashboard with both is one request for each.
 *
 * A card is a button where pressing it opens exactly the cases it counts: the
 * request of 7 October 2026, section 7, and since the lab's request of
 * 17 October 2026, item 1, every one of them: Orders asks the server for the
 * urgent alone now.
 */
export function CaseCountsWidget() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const holdsOnHold = session?.views.includes('onhold') === true;

  const active = useJobOrders(ACTIVE);
  const dueToday = useJobOrders(CASE_LISTS.dueToday.filter);
  const overdue = useJobOrders(CASE_LISTS.overdue.filter);
  const urgent = useJobOrders(CASE_LISTS.urgent.filter);
  const onHold = useJobOrders(CASE_LISTS.onHold.filter);

  return (
    <WidgetSection label={text.kicker.cases} title={text.widgets.caseCounts.title}>
      <KpiStrip label={text.counts.label}>
        <Kpi label={text.counts.active} value={countOf(active)} onPress={() => void navigate('/orders?status=active')} />
        <Kpi
          label={text.counts.dueToday}
          value={countOf(dueToday)}
          onPress={() => void navigate(CASE_LISTS.dueToday.all)}
        />
        <Kpi
          label={text.counts.overdue}
          value={countOf(overdue)}
          alert={(overdue.data?.total ?? 0) > 0}
          onPress={() => void navigate(CASE_LISTS.overdue.all)}
        />
        <Kpi label={text.counts.urgent} value={countOf(urgent)} onPress={() => void navigate(CASE_LISTS.urgent.all)} />
        <Kpi
          label={text.counts.onHold}
          value={countOf(onHold)}
          onPress={holdsOnHold ? () => void navigate(CASE_LISTS.onHold.all) : undefined}
        />
      </KpiStrip>
    </WidgetSection>
  );
}

/** A list's size: undefined while it is on its way, null where it could not be read. */
function countOf(query: { data?: { total: number }; isError: boolean }): number | null | undefined {
  if (query.data !== undefined) return query.data.total;
  return query.isError ? null : undefined;
}

/* Each list as a widget of its own, named for the list: what the registry draws. */

export function DueTodayWidget() {
  return <CaseListWidget list="dueToday" />;
}

export function OverdueWidget() {
  return <CaseListWidget list="overdue" />;
}

export function UrgentWidget() {
  return <CaseListWidget list="urgent" />;
}

export function LatestCasesWidget() {
  return <CaseListWidget list="latestCases" />;
}

export function ReadyToShipWidget() {
  return <CaseListWidget list="readyToShip" />;
}

export function OnHoldWidget() {
  return <CaseListWidget list="onHold" />;
}

export function ToBePaidWidget() {
  return <CaseListWidget list="toBePaid" />;
}

export function PartPaidWidget() {
  return <CaseListWidget list="partPaid" />;
}

export function ChangedCasesWidget() {
  return <CaseListWidget list="changedCases" />;
}

export function ReleasedWidget() {
  return <CaseListWidget list="released" />;
}

export function InCeramicWidget() {
  return <CaseListWidget list="inCeramic" />;
}
